// src/components/engine/DecisionTimeline.jsx — v9.5.2
// Canonical: first event expanded, rest collapsible.
// No engine-mode labels. No confidence labels. Forensic tone only.

import { useState, useEffect } from "react";
import IncidentReportTrigger from "./IncidentReport";
import LossAttributionBlock  from "./LossAttributionBlock";
import { workerClient }       from "../../api/workerClient";

const EVENT_COLORS = {
  decision:      { bg:"rgba(60,130,230,0.07)",  border:"rgba(60,130,230,0.22)",  dot:"#3c82e6" },
  action:        { bg:"rgba(232,85,85,0.07)",   border:"rgba(232,85,85,0.22)",   dot:"#e85555" },
  observation:   { bg:"rgba(200,200,208,0.07)", border:"rgba(200,200,208,0.18)", dot:"#c8c8d0" },
  risk_update:   { bg:"rgba(245,166,35,0.07)",  border:"rgba(245,166,35,0.18)",  dot:"#f5a623" },
  signal_change: { bg:"rgba(255,255,255,0.03)", border:"rgba(255,255,255,0.07)", dot:"#4b5a6e" },
  override:      { bg:"rgba(18,204,118,0.05)",  border:"rgba(18,204,118,0.18)",  dot:"#12cc76" },
};

const TYPE_LABELS = {
  decision:      "Performance issue detected",
  action:        "Action taken",
  observation:   "Monitoring",
  risk_update:   "Performance change",
  signal_change: "Signal change",
  override:      "Manual override",
};

const ACTION_HUMAN = {
  pause:   "Campaign paused",
  reduce:  "Spend reduced",
  alert:   "Alert raised",
  observe: "Monitoring",
  none:    "",
};

const SEVERITY_LABELS = {
  critical: "Severe drop",
  high:     "Significant drop",
  medium:   "Mild degradation",
  low:      "Within normal range",
};

const SEVERITY_COLORS = {
  critical: "#d95252",
  high:     "#d95252",
  medium:   "#e9a020",
  low:      "#12cc76",
};

function fmtTs(ts) {
  if (!ts) return "—";
  return new Date(Number(ts) * 1000).toLocaleString("en-GB",
    { month:"short", day:"2-digit", hour:"2-digit", minute:"2-digit" });
}

function EventCard({ event, defaultExpanded }) {
  const [open, setOpen] = useState(defaultExpanded ?? false);
  const type    = event.event_type ?? "decision";
  const col     = EVENT_COLORS[type] ?? EVENT_COLORS.decision;
  const hasBody = (event.reasons?.length > 0) || event.message;
  const actionLabel  = ACTION_HUMAN[event.action] ?? "";
  const severityLabel = SEVERITY_LABELS[event.risk_status];
  const severityColor = SEVERITY_COLORS[event.risk_status] ?? "var(--text-muted)";

  return (
    <div style={{ borderRadius:"var(--radius)", background:col.bg, border:`1px solid ${col.border}`, overflow:"hidden" }}>
      {/* Header row */}
      <div
        onClick={() => hasBody && setOpen(x => !x)}
        style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", cursor:hasBody ? "pointer" : "default" }}
      >
        <div style={{ width:7, height:7, borderRadius:"50%", background:col.dot, boxShadow:`0 0 5px ${col.dot}60`, flexShrink:0 }} />

        <span style={{ fontFamily:"var(--font-mono)", fontSize:10, color:col.dot, fontWeight:600, flexShrink:0 }}>
          {TYPE_LABELS[type] ?? type}
        </span>

        {actionLabel && (
          <span style={{ fontFamily:"var(--font-mono)", fontSize:9, color:"var(--text-muted)", flexShrink:0 }}>
            · {actionLabel}
          </span>
        )}

        {event.prevented_loss > 0 && (
          <span style={{ fontFamily:"var(--font-mono)", fontSize:9, color:"var(--success)", fontWeight:700, flexShrink:0 }}>
            −€{Math.round(event.prevented_loss).toLocaleString("en-US")} avoidable
          </span>
        )}

        {severityLabel && (
          <span style={{ fontFamily:"var(--font-mono)", fontSize:9, color:severityColor, flexShrink:0 }}>
            · {severityLabel}
          </span>
        )}

        {type === "override" && (
          <span style={{ fontFamily:"var(--font-mono)", fontSize:9, color:"#12cc76", flexShrink:0 }}>
            Manually overridden
          </span>
        )}

        <div style={{ flex:1 }} />

        <span style={{ fontFamily:"var(--font-mono)", fontSize:9, color:"var(--text-muted)", flexShrink:0 }}>
          {fmtTs(event.timestamp)}
        </span>
        {hasBody && (
          <span style={{ fontFamily:"var(--font-mono)", fontSize:8, color:"var(--text-muted)", marginLeft:4 }}>
            {open ? "▲" : "▼"}
          </span>
        )}
      </div>

      {/* Expanded body */}
      {open && hasBody && (
        <div style={{ padding:"0 14px 12px 31px", borderTop:`1px solid ${col.border}`, paddingTop:10 }}>

          {event.reasons?.length > 0 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9, letterSpacing:"0.10em",
                            textTransform:"uppercase", color:"var(--text-muted)", marginBottom:6 }}>
                Evidence
              </div>
              {event.reasons.map((r, i) => (
                <div key={i} style={{ display:"flex", gap:6, marginBottom:4 }}>
                  <span style={{ color:"var(--danger)", flexShrink:0, marginTop:1 }}>↓</span>
                  <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-secondary)", lineHeight:1.55 }}>{r}</span>
                </div>
              ))}
            </div>
          )}

          {!event.reasons?.length && type === "observation" && (
            <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-muted)", lineHeight:1.65 }}>
              Performance signals were outside normal range, but more data is needed before
              recommending action.
            </div>
          )}

          {!event.reasons?.length && event.message && type !== "observation" && (
            <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-muted)" }}>{event.message}</div>
          )}

          {event.reasons?.length > 0 && type !== "override" && (
            <div style={{ marginTop:8 }}>
              <LossAttributionBlock reasons={event.reasons} risk_status={event.risk_status} root_cause={event.root_cause ?? ""} compact={true} />
            </div>
          )}

          <IncidentReportTrigger event={event} />
        </div>
      )}
    </div>
  );
}

export default function DecisionTimeline({
  campaignId,
  events: propEvents,
  limit    = 50,
  compact  = false,
  groupBy  = false,
  title    = "",
}) {
  const [events,  setEvents]  = useState(propEvents ?? []);
  const [loading, setLoading] = useState(!propEvents);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (propEvents) { setEvents(propEvents); return; }
    if (!campaignId) { setLoading(false); return; }
    workerClient(`/api/engine/timeline?campaign_id=${campaignId}&limit=${limit}`)
      .then(r => { if (r.ok) setEvents(r.events ?? []); else setError("Could not load timeline"); })
      .catch(() => setError("Network error"))
      .finally(() => setLoading(false));
  }, [campaignId, limit]);

  const shown = compact ? events.slice(0, 5) : events;

  if (loading) return (
    <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
      {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height:36, borderRadius:"var(--radius)" }} />)}
    </div>
  );

  if (error) return (
    <div style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--danger)" }}>{error}</div>
  );

  if (!shown.length) return (
    <div style={{ padding:"14px 18px", borderRadius:"var(--radius)", background:"var(--bg-card)",
                  border:"1px solid var(--border-subtle)", fontFamily:"var(--font-mono)",
                  fontSize:11, color:"var(--text-muted)", textAlign:"center" }}>
      No performance events in this period
    </div>
  );

  if (title && !compact) {
    // render title label
  }

  // First event expanded, rest collapsible
  const [first, ...rest] = shown;

  return (
    <div>
      {title && !compact && (
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9, letterSpacing:"0.14em",
                      textTransform:"uppercase", color:"var(--text-muted)", marginBottom:10 }}>
          {title}
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
        {/* First event always expanded */}
        <EventCard key={first.id ?? 0} event={first} defaultExpanded={!compact} />

        {/* Remaining events in native collapsible */}
        {rest.length > 0 && (
          <details style={{ marginTop:2 }}>
            <summary style={{
              fontFamily:"var(--font-mono)", fontSize:9, color:"var(--text-muted)",
              cursor:"pointer", padding:"6px 2px", letterSpacing:"0.08em",
              listStyle:"none", userSelect:"none",
            }}>
              {rest.length} more event{rest.length !== 1 ? "s" : ""} ▼
            </summary>
            <div style={{ display:"flex", flexDirection:"column", gap:4, marginTop:6 }}>
              {rest.map((e, i) => (
                <EventCard key={e.id ?? i + 1} event={e} defaultExpanded={false} />
              ))}
            </div>
          </details>
        )}
      </div>

      {compact && events.length > 5 && (
        <div style={{ fontFamily:"var(--font-mono)", fontSize:9, color:"var(--text-muted)", textAlign:"center", marginTop:6 }}>
          +{events.length - 5} more events in full report
        </div>
      )}
    </div>
  );
}
